import { OrganizationType } from '@upright/common/iam/organizations/organization-types';
import {
  type ClimateTrialAudience,
  DASHBOARD_MODAL_PARAM,
  pickConsultantTrialCompany,
} from '@upright/ui/views/climate-trial-view/resolve-climate-trial-destination';
import { SUPPLIERS_BASE_PATH } from '@upright/ui/views/dma-v2-view/dashboard/suppliers/constants';

/**
 * Path of the supplier-trial campaign link (PLT-7200). Handed out as a bare URL
 * in outbound material, so it must stay stable once published.
 */
export const SUPPLIER_TRIAL_PATH = '/go/supplier-trial';

/**
 * Marks the `/dashboard` arrival that should open the "create a trial company"
 * modal with the supplier wording. Read and consumed by `ConsultantDashboardView`,
 * beside the climate link's own value for the same modal.
 */
export const SUPPLIER_TRIAL_MODAL = 'supplier-trial';

/**
 * Where a reader of the supplier-trial campaign link should land: the Suppliers
 * page of the company whose suppliers they would assess.
 *
 * Resolved at click time for the same reason as the climate link — the audience is
 * mixed and only the platform knows which of them the reader is. The dashboard
 * mounts at two bases (`/dashboard` for a freemium user, `/company/:id/:name/dashboard`
 * for everyone else), so the destination has to be chosen per audience too.
 *
 * The supplier allowance is counted per buyer company, so a consultant's own trial
 * company is the right target for the same reason it is for climate: a client's
 * company would spend that client's places.
 */
export function resolveSupplierTrialDestination(audience: ClimateTrialAudience): string {
  const { organizationType, companies } = audience;

  if (organizationType === OrganizationType.DMA_CONSULTANT) {
    const company = pickConsultantTrialCompany(companies);
    if (company === null) {
      return `/dashboard?${DASHBOARD_MODAL_PARAM}=${SUPPLIER_TRIAL_MODAL}`;
    }
    // The `:name` segment is only cosmetic, but an empty one would not match the route, so a
    // company with no name falls back to its id.
    const name = company.companyName?.trim();
    const nameSegment = encodeURIComponent(name === undefined || name === '' ? company.companyId : name);
    return `/company/${company.companyId}/${nameSegment}/dashboard/${SUPPLIERS_BASE_PATH}`;
  }

  // A freemium user's company is implicit from their organization, so their
  // dashboard is the bare `/dashboard` (see `main-navigation.tsx`).
  if (organizationType === OrganizationType.DMA_FREEMIUM) {
    return `/dashboard/${SUPPLIERS_BASE_PATH}`;
  }

  // Investors, corporate customers and internal users have no Suppliers page of
  // their own, and an unresolved audience has nowhere better to go: the root
  // sends each reader to their own home.
  return '/';
}

/**
 * Whether the reader is known to have an account type the trial does not cover, so the
 * link sends them home and should say why (PLT-7567). An unresolved audience is not
 * told: it may well be one the trial covers.
 */
export function isSupplierTrialUnsupportedAudience({ organizationType }: ClimateTrialAudience): boolean {
  return (
    organizationType !== undefined &&
    organizationType !== OrganizationType.DMA_CONSULTANT &&
    organizationType !== OrganizationType.DMA_FREEMIUM
  );
}
